import { useState } from 'react';
import { ChevronDown, Camera } from 'lucide-react';
import type { PageAudit } from '@/types/sem';
import { AnnotatedScreenshot } from './AnnotatedScreenshot';
import { fmtMoney } from './story-utils';

interface AnnotatedAuditProps {
  audit: PageAudit;
}

export function AnnotatedAudit({ audit }: AnnotatedAuditProps) {
  const [open, setOpen] = useState(false);

  const criticalCount = audit.annotations.filter(a => a.severity === 'critical').length;
  const warningCount = audit.annotations.filter(a => a.severity === 'warning').length;
  const additionCount = audit.annotations.filter(a => a.severity === 'addition').length;

  // Time since analysis
  const hoursAgo = Math.round(
    (Date.now() - new Date(audit.analyzed_at).getTime()) / (1000 * 60 * 60)
  );

  return (
    <div className="audit-section story-anim-2">
      {/* Collapsible header */}
      <button
        onClick={() => setOpen(!open)}
        className="audit-topbar"
        style={{ cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Camera size={14} style={{ opacity: 0.5 }} />
          <span className="audit-topbar-count">
            {audit.issue_count} issues identified
          </span>
          <span style={{ display: 'inline-flex', gap: 6, marginLeft: 4 }}>
            {criticalCount > 0 && (
              <span className="audit-legend-item" style={{ fontSize: '0.65rem' }}>
                <span className="audit-legend-dot" style={{ background: 'var(--brick)' }} />
                {criticalCount}
              </span>
            )}
            {warningCount > 0 && (
              <span className="audit-legend-item" style={{ fontSize: '0.65rem' }}>
                <span className="audit-legend-dot" style={{ background: 'var(--brass)' }} />
                {warningCount}
              </span>
            )}
            {additionCount > 0 && (
              <span className="audit-legend-item" style={{ fontSize: '0.65rem' }}>
                <span className="audit-legend-dot" style={{ background: 'var(--sage)' }} />
                {additionCount}
              </span>
            )}
          </span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="audit-topbar-revenue">
            Revenue at risk {audit.total_revenue_at_risk < 0 ? '−' : '+'}
            {fmtMoney(Math.abs(audit.total_revenue_at_risk))}/mo
          </span>
          <ChevronDown
            size={14}
            style={{
              opacity: 0.4,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </span>
      </button>

      {/* Collapsible body */}
      {open && (
        <>
          {/* Two-column grid */}
          <div className="audit-grid">
            {/* Left: Screenshot with annotations */}
            <div className="audit-screenshot-col">
              <div className="audit-col-header">Page Capture</div>
              <AnnotatedScreenshot
                screenshotPath={audit.screenshot_path}
                elementMap={audit.element_map}
                annotations={audit.annotations}
                foldY={audit.fold_y}
                viewportWidth={audit.viewport_width}
              />
            </div>

            {/* Right: Findings list */}
            <div className="audit-findings-col">
              <div className="audit-col-header">Findings</div>
              {audit.annotations.map((ann) => {
                const revenueClass = `audit-finding-revenue-${ann.severity}`;
                const isPositive = ann.revenue_impact > 0;

                return (
                  <div className="audit-finding" key={ann.zone_index}>
                    <span className="audit-finding-num">{ann.zone_index}</span>
                    <div className="audit-finding-body">
                      <div className="audit-finding-header">
                        <span className="audit-finding-title">{ann.title}</span>
                        <span className={`audit-finding-revenue ${revenueClass}`}>
                          {isPositive ? '+' : '−'}{fmtMoney(Math.abs(ann.revenue_impact))}
                        </span>
                      </div>
                      <div className="audit-finding-desc">{ann.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="audit-footer">
            <div className="audit-legend">
              {criticalCount > 0 && (
                <span className="audit-legend-item">
                  <span className="audit-legend-dot" style={{ background: 'var(--brick)' }} />
                  {criticalCount} critical
                </span>
              )}
              {warningCount > 0 && (
                <span className="audit-legend-item">
                  <span className="audit-legend-dot" style={{ background: 'var(--brass)' }} />
                  {warningCount} warning
                </span>
              )}
              {additionCount > 0 && (
                <span className="audit-legend-item">
                  <span className="audit-legend-dot" style={{ background: 'var(--sage)' }} />
                  {additionCount} recommended
                </span>
              )}
            </div>
            <span className="audit-footer-time">
              Captured {hoursAgo}h ago
            </span>
          </div>
        </>
      )}
    </div>
  );
}
